/**
 * <s-metal-ticker data-api-key="…"> fills the header price bar with live spot prices (EUR per troy
 * ounce) from metalpriceapi.com and today's change against the price at midnight (Bratislava time). Each
 * <li data-metal="XAU|XAG|XPT|XPD"> holds [data-metal-price] and [data-metal-change].
 * Refreshes when <s-price-countdown> finishes (event "gv:price-refresh"). Latest prices are cached
 * in localStorage for just under the countdown so open tabs share one request; the midnight price is
 * cached for the day.
 */
const LATEST_KEY = 'gv-spot-ticker-latest';
const LATEST_TTL = 170 * 1000;
const CLOSE_KEY = 'gv-spot-ticker-close';
const SYMBOLS = ['XAU', 'XAG', 'XPT', 'XPD'];
const API = 'https://api.metalpriceapi.com/v1/';
const TIME_ZONE = 'Europe/Bratislava';

function perOunce(rates, symbol) {
  const direct = Number(rates?.[`EUR${symbol}`]);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const inverse = Number(rates?.[symbol]);
  return inverse > 0 ? 1 / inverse : NaN;
}

function readCache(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

async function getJson(url) {
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data && data.success ? data : null;
  } catch {
    return null;
  }
}

class MetalTicker extends HTMLElement {
  #onRefresh = () => this.#load(true);

  connectedCallback() {
    window.addEventListener('gv:price-refresh', this.#onRefresh);
    this.#load(false);
  }

  disconnectedCallback() {
    window.removeEventListener('gv:price-refresh', this.#onRefresh);
  }

  async #latest(force) {
    const cached = readCache(LATEST_KEY);
    const fresh = cached && Date.now() - cached.time < (force ? LATEST_TTL - 20000 : LATEST_TTL);
    if (fresh && cached.data) return cached.data;
    const apiKey = encodeURIComponent(this.dataset.apiKey || '');
    const data = await getJson(`${API}latest?api_key=${apiKey}&base=EUR&currencies=${SYMBOLS.join(',')}`);
    if (data) writeCache(LATEST_KEY, { time: Date.now(), data });
    return data || cached?.data || null;
  }

  // Reference price for "today's change": the first hourly price at or after midnight in Bratislava
  // (the 00:00 hourly point, i.e. the price at the start of the trading day around 0:05). Cached per local day.
  async #close(latest) {
    const now = Number(latest?.timestamp) > 0 ? new Date(latest.timestamp * 1000) : new Date();
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
        .formatToParts(now)
        .map((p) => [p.type, p.value]),
    );
    const localDate = `${parts.year}-${parts.month}-${parts.day}`;
    const cached = readCache(CLOSE_KEY);
    if (cached && cached.date === localDate && cached.mode === 'midnight' && cached.data) return cached.data;

    const sinceMidnight = (Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second)) * 1000;
    const midnight = Math.floor((now.getTime() - sinceMidnight) / 1000);
    const startDate = new Date((midnight - 3600) * 1000).toISOString().slice(0, 10);
    const endDate = now.toISOString().slice(0, 10);
    const apiKey = encodeURIComponent(this.dataset.apiKey || '');

    const rates = {};
    await Promise.all(
      SYMBOLS.map(async (symbol) => {
        const data = await getJson(`${API}hourly?api_key=${apiKey}&base=EUR&currency=${symbol}&start_date=${startDate}&end_date=${endDate}`);
        const point = (data?.rates || [])
          .filter((r) => Number(r.timestamp) >= midnight)
          .sort((a, b) => a.timestamp - b.timestamp)[0];
        const price = perOunce(point?.rates, symbol);
        if (Number.isFinite(price)) rates[`EUR${symbol}`] = price;
      }),
    );
    if (Object.keys(rates).length === SYMBOLS.length) {
      const data = { rates };
      writeCache(CLOSE_KEY, { date: localDate, mode: 'midnight', data });
      return data;
    }

    // Fallback (e.g. right after midnight before the first hourly point exists): previous day's close.
    const yesterday = new Date(now.getTime() - 86400000).toISOString().slice(0, 10);
    const fallback = await getJson(`${API}${yesterday}?api_key=${apiKey}&base=EUR&currencies=${SYMBOLS.join(',')}`);
    if (fallback) Object.keys(rates).forEach((key) => (fallback.rates[key] = rates[key]));
    return fallback || (Object.keys(rates).length ? { rates } : null);
  }

  async #load(force) {
    if (!this.dataset.apiKey) return;
    const latest = await this.#latest(force);
    if (!latest || !this.isConnected) return;
    const close = await this.#close(latest);
    const price = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const percent = new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero' });
    // Share today's change per metal with <s-metal-trend> next to product prices (no extra API calls).
    const trends = {};
    for (const symbol of SYMBOLS) {
      const now = perOunce(latest.rates, symbol);
      const before = perOunce(close?.rates, symbol);
      if (Number.isFinite(now) && Number.isFinite(before)) trends[symbol] = Math.round((now / before - 1) * 10000) / 100;
    }
    window.gvMetalTrends = trends;
    window.dispatchEvent(new CustomEvent('gv:metal-trends', { detail: trends }));
    this.querySelectorAll('[data-metal]').forEach((item) => {
      const symbol = item.dataset.metal;
      const now = perOunce(latest.rates, symbol);
      const priceEl = item.querySelector('[data-metal-price]');
      if (priceEl && Number.isFinite(now)) priceEl.textContent = price.format(now);
      const changeEl = item.querySelector('[data-metal-change]');
      const before = perOunce(close?.rates, symbol);
      if (!changeEl) return;
      if (!Number.isFinite(now) || !Number.isFinite(before)) {
        changeEl.hidden = true;
        return;
      }
      const pct = (now / before - 1) * 100;
      const rounded = Math.round(pct * 100) / 100;
      const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
      changeEl.hidden = false;
      changeEl.classList.toggle('text-price-up', direction === 'up');
      changeEl.classList.toggle('text-price-down', direction === 'down');
      changeEl.classList.toggle('text-on-surface-secondary', direction === 'flat');
      const arrow = changeEl.querySelector('[data-arrow]');
      if (arrow) arrow.textContent = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■';
      const value = changeEl.querySelector('[data-value]');
      if (value) value.textContent = `${percent.format(rounded)} %`;
      const label = changeEl.querySelector('[data-label]');
      if (label) label.textContent = this.dataset[`label${direction[0].toUpperCase()}${direction.slice(1)}`] || '';
    });
  }
}

if (!customElements.get('s-metal-ticker')) customElements.define('s-metal-ticker', MetalTicker);
